import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { forgetOpenPending, getChatHistory } from '../services/chatService';
import { MessageActionHandler } from '../utils/messageActionHandler';
import { useAuth } from './AuthContext';

const ChatPanelContext = createContext(null);

// The full chat page, where the panel's "Open in full chat page" takes the chat.
const CHAT_PAGE = '/chat';

export const useChatPanel = () => {
  const context = useContext(ChatPanelContext);
  if (context === undefined) {
    throw new Error('useChatPanel must be used within a ChatPanelProvider');
  }
  return context;
};

/** The chat panel's context, or null outside a ChatPanelProvider (pages rendered on their own). */
export const useOptionalChatPanel = () => useContext(ChatPanelContext) || null;

/** The rows of a GET /api/chat checkpoint as chat entries. */
const mapHistoryRows = (rows = []) => (Array.isArray(rows) ? rows : []).map((msg) => ({
  id: msg.id || null,
  type: msg.role === 'user' ? 'user' : 'ai',
  text: MessageActionHandler.processMessageAction(
    msg.content,
    msg.structured_response?.action
  ),
  message_type: msg.message_type || '',
  action: msg.structured_response?.action,
  visualizations: Array.isArray(msg.structured_response?.visualizations)
    ? msg.structured_response.visualizations
    : [],
  // Cards for what the app made (show_artifact); older messages have none.
  artifacts: Array.isArray(msg.structured_response?.artifacts)
    ? msg.structured_response.artifacts
    : [],
  feedback_value: msg.feedback_value || null,
  created_at: msg.created_at || null,
  // The outcome line of a confirmed action names the confirm card it closes.
  ...(msg.pending_outcome && typeof msg.pending_outcome === 'object'
    ? { pending_outcome: msg.pending_outcome }
    : {}),
}));

/**
 * The chat in a GET /api/chat reply: the messages of its latest checkpoint, its thread id, and
 * whether a visitor's free messages are used up. The server picks the thread: the signed-in
 * user's own, or the visitor's (the call also sets the visitor's cookie).
 */
const readHistory = (history) => {
  const historyData = history?.data || history;
  const checkpoints = historyData?.checkpoints || [];
  const checkpoint = checkpoints[checkpoints.length - 1];
  return {
    conversation: mapHistoryRows(checkpoint?.messages || []),
    threadId: historyData?.thread_id || null,
    tokenLimitReached: historyData?.visitor_token_info?.limit_reached === true,
  };
};

/** Load the chat (GET /api/chat). */
const fetchConversation = async (options = {}) => readHistory(await getChatHistory(options));

/**
 * IMPROVED: Enhanced ChatPanelContext as single source of truth
 * Manages all conversation state, loading, and initialization
 *
 * Each user has one chat, one conversation (`threadId`). Every message goes out with the page
 * it is about (`getPage`), so KeyTa knows which property "this" means for that message.
 */
export const ChatPanelProvider = ({ children }) => {
  const { state: authState } = useAuth();
  const [chatPanelState, setChatPanelState] = useState({
    isVisible: false,
    isMinimized: false,
    conversation: [],
    threadId: null,
    sourceRoute: null,
    currentParams: null,
    width: 500,
    loading: false,
    isAiThinking: false, // Combined loading state for field help and chat
    isInitialized: false,
    visitorTokenLimitReached: false,
  });
  // Whether the page on screen docks the panel (PageShell `chat`). The panel is drawn once, above
  // the routes (MultiModalLayout.js::DockedChatPanel), so it is not built again on every page; the
  // last page's answer holds while the next one loads, so it does not flicker in between.
  const [pageDocksChat, setPageDocksChat] = useState(false);
  // Bumped by every load of the chat, so a slow one never lands on another account's.
  const loadRequestRef = useRef(0);
  // Bumped when the account changes (login, logout, another account): a reply still streaming
  // for the previous one stops writing to the chat (hooks/useChat.js).
  const chatEpochRef = useRef(0);
  const getChatEpoch = useCallback(() => chatEpochRef.current, []);
  // What the user has typed and not sent yet. The panel mounts again on every page, so the text is
  // kept here (a ref: typing re-renders nothing else) and dropped when the account changes.
  const draftRef = useRef('');
  const getDraft = useCallback(() => draftRef.current, []);
  const setDraft = useCallback((text) => { draftRef.current = text; }, []);

  const navigate = useNavigate();
  const location = useLocation();
  // The page a message is about: the one the user is on (not sourceRoute, which holds where the
  // panel was opened), or on the full chat page the last page they were on before it.
  const pageRef = useRef(location.pathname === CHAT_PAGE ? null : location.pathname);
  useEffect(() => {
    if (location.pathname !== CHAT_PAGE) pageRef.current = location.pathname;
  }, [location.pathname]);

  /** The path of the page a message is about, read when it is sent; null when there is none. */
  const getPage = useCallback(() => pageRef.current || null, []);

  // Load the chat once auth is known, and again for the other account on login, logout or a switch
  // straight from one account to another (another tab, or a login while signed in). The email tells
  // accounts apart: the user kept in storage has no id (utils/secureStorage.js::sanitizeUserData).
  const accountKey = authState.user?.email ?? null;
  useEffect(() => {
    if (!authState.isInitialized) return undefined;
    const request = loadRequestRef.current + 1;
    loadRequestRef.current = request;
    chatEpochRef.current += 1;
    draftRef.current = '';
    const controller = new AbortController();
    // What the previous account had open is not this one's.
    forgetOpenPending();

    // The previous account's chat leaves the screen now, so a failed load never shows it to the
    // next one; nor do its visitor limit and a reply it was still waiting for.
    setChatPanelState(prev => ({
      ...prev, conversation: [], threadId: null, loading: true, isAiThinking: false,
      visitorTokenLimitReached: false,
    }));

    fetchConversation({ signal: controller.signal })
      .then((loaded) => {
        if (controller.signal.aborted || request !== loadRequestRef.current) return;
        setChatPanelState(prev => ({
          ...prev,
          conversation: loaded.conversation,
          threadId: loaded.threadId,
          loading: false,
          isInitialized: true,
          visitorTokenLimitReached: loaded.tokenLimitReached,
        }));
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || controller.signal.aborted || request !== loadRequestRef.current) return;
        console.error('ChatPanelProvider: Failed to load the chat:', error);
        setChatPanelState(prev => ({ ...prev, loading: false, isInitialized: true }));
      });

    return () => controller.abort();
  }, [authState.isInitialized, authState.isAuthenticated, accountKey]);

  /**
   * The outcome line of a confirmed action as KeyTa wrote it into the chat, or null. The chat on
   * screen is not replaced.
   */
  const findOutcomeLine = useCallback(async (pendingId) => {
    const loaded = readHistory(await getChatHistory());
    return loaded.conversation.find((row) => row.pending_outcome?.pending_id === pendingId) || null;
  }, []);

  // Show chat panel (when coming from action or manual activation)
  const showChatPanel = useCallback((sourceRoute = null, params = null, options = {}) => {
    const { startMinimized = false } = options;

    setChatPanelState(prev => ({
      ...prev,
      isVisible: true,
      isMinimized: startMinimized,
      sourceRoute: sourceRoute || location.pathname,
      currentParams: params,
    }));
  }, [location.pathname]);

  // Hide chat panel completely
  const hideChatPanel = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isVisible: false,
      isMinimized: false,
      sourceRoute: null,
      currentParams: null,
    }));
  }, []);

  // Toggle minimized state
  const toggleMinimizeChat = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isMinimized: !prev.isMinimized,
    }));
  }, []);

  // Restore chat panel from minimized state
  const restoreChatPanel = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isVisible: true,
      isMinimized: false,
    }));
  }, []);

  // Navigate with chat context (for actions)
  const navigateWithChat = useCallback((targetPath, params = null) => {
    // Full URLs (http/https) open in a new tab as-is
    if (targetPath.startsWith('http://') || targetPath.startsWith('https://')) {
      window.open(targetPath, '_blank', 'noopener,noreferrer');
      return;
    }

    // Relative paths navigate in the same tab
    showChatPanel(location.pathname, params);

    // Build URL with params
    const url = params ? `${targetPath}?${params}` : targetPath;

    // Navigate to target
    navigate(url, {
      state: {
        showChat: true,
        sourceRoute: location.pathname,
        chatParams: params
      }
    });
  }, [navigate, location.pathname, showChatPanel]);

  // Go back to chat-only view. The chat goes along whole, still about the page it came from.
  const returnToChat = useCallback(() => {
    navigate(CHAT_PAGE, {
      state: {
        fromMultiModal: true,
        preserveConversation: true
      }
    });
    hideChatPanel();
  }, [navigate, hideChatPanel]);

  // Update conversation
  const updateConversation = useCallback((newConversationOrUpdater) => {
    setChatPanelState(prev => {
      const prevConversation = Array.isArray(prev.conversation) ? prev.conversation : [];
      const newConversation = typeof newConversationOrUpdater === 'function'
        ? newConversationOrUpdater(prevConversation)
        : newConversationOrUpdater;

      // Ensure the new conversation is always an array
      const safeNewConversation = Array.isArray(newConversation) ? newConversation : [];

      // console.log('🔄 ChatPanelContext updateConversation called:');
      // console.log('  - Previous conversation length:', prevConversation.length);
      // console.log('  - New conversation length:', safeNewConversation.length);
      // console.log('  - Is function updater:', typeof newConversationOrUpdater === 'function');

      return {
        ...prev,
        conversation: safeNewConversation,
      };
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((loading) => {
    setChatPanelState(prev => ({
      ...prev,
      loading
    }));
  }, []);

  // Set AI thinking state (for field help and chat loading)
  const setAiThinking = useCallback((isAiThinking) => {
    setChatPanelState(prev => ({
      ...prev,
      isAiThinking
    }));
  }, []);

  // Mark visitor as having reached the token limit
  const setVisitorTokenLimitReached = useCallback((reached) => {
    setChatPanelState(prev => ({
      ...prev,
      visitorTokenLimitReached: reached,
    }));
  }, []);

  // Clear conversation
  const clearConversation = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      conversation: []
    }));
  }, []);

  // Resize chat panel
  const resizeChatPanel = useCallback((newWidth) => {
    setChatPanelState(prev => ({
      ...prev,
      width: Math.max(300, Math.min(600, newWidth)), // Min 300px, max 600px
    }));
  }, []);

  // Check if current route should show chat panel
  const shouldShowChatPanel = useCallback(() => {
    const state = location.state;
    return chatPanelState.isVisible || state?.showChat;
  }, [chatPanelState.isVisible, location.state]);

  // Load the chat again (as on page load).
  const refreshConversation = useCallback(async () => {
    if (!authState.isInitialized) return;
    const request = loadRequestRef.current + 1;
    loadRequestRef.current = request;
    setChatPanelState(prev => ({ ...prev, loading: true }));
    try {
      const loaded = await fetchConversation();
      if (request !== loadRequestRef.current) return;
      setChatPanelState(prev => ({
        ...prev,
        conversation: loaded.conversation,
        threadId: loaded.threadId || prev.threadId,
        loading: false,
      }));
    } catch (error) {
      console.error('ChatPanelProvider: Failed to refresh the chat:', error);
      if (request === loadRequestRef.current) {
        setChatPanelState(prev => ({ ...prev, loading: false }));
      }
    }
  }, [authState.isInitialized]);

  const contextValue = {
    // State
    chatPanelState,

    // Actions
    showChatPanel,
    hideChatPanel,
    toggleMinimizeChat,
    restoreChatPanel,
    navigateWithChat,
    returnToChat,
    updateConversation,
    setLoading,
    setAiThinking,
    setVisitorTokenLimitReached,
    clearConversation,
    resizeChatPanel,
    refreshConversation,
    findOutcomeLine,

    // The page a message is about, read when it is sent
    getPage,
    // Which account's chat is on screen (changes on every account change)
    getChatEpoch,
    // The text typed and not sent yet, kept across page changes
    getDraft,
    setDraft,
    // Whether the page on screen docks the panel (set by PageShell)
    pageDocksChat,
    setPageDocksChat,

    // Computed
    shouldShowChatPanel,

    // Utils
    isCurrentlyInMultiModal: shouldShowChatPanel() && location.pathname !== CHAT_PAGE,
  };

  return (
    <ChatPanelContext.Provider value={contextValue}>
      {children}
    </ChatPanelContext.Provider>
  );
};

export default ChatPanelContext;
